<template>
  <div id="vendasOnlinePrevisao">
    <q-banner v-if="erro" dense class="bg-red-1 text-negative q-mb-md" data-test="previsao-erro">{{ erro }}</q-banner>

    <q-table
      dense
      :rows="dias"
      :columns="columns"
      row-key="data"
      :loading="carregando"
      :pagination="{ rowsPerPage: 0 }"
      hide-pagination
      no-data-label="Nenhum crédito previsto no período"
      loading-label="Carregando dados"
    >
      <template v-slot:top="props">
        <div>
          <div class="q-table__title">Previsão de crédito</div>
          <div class="text-caption text-grey-7">Pix e Boleto/Pix caem na conta no dia útil seguinte ao pagamento.</div>
        </div>
        <q-space />

        <q-btn-group rounded flat no-wrap no-caps class="q-mr-sm">
          <q-btn class="white text-blue-7" rounded icon="chevron_left" data-test="previsao-anterior" @click="deslocar(-1)" />
          <q-btn class="white text-blue-7" rounded no-caps :label="periodo.inicio || 'Início'" data-test="previsao-inicio">
            <q-popup-proxy ref="inicio" transition-show="scale" transition-hide="scale">
              <q-date
                :model-value="periodo.inicio"
                :mask="MASCARA"
                :locale="langDate"
                minimal
                today-btn
                @update:model-value="(valor) => alterar('inicio', valor)"
              />
            </q-popup-proxy>
          </q-btn>
          <q-btn class="white text-blue-7" rounded no-caps :label="periodo.fim || 'Fim'" data-test="previsao-fim">
            <q-popup-proxy ref="fim" transition-show="scale" transition-hide="scale">
              <q-date
                :model-value="periodo.fim"
                :mask="MASCARA"
                :locale="langDate"
                minimal
                today-btn
                @update:model-value="(valor) => alterar('fim', valor)"
              />
            </q-popup-proxy>
          </q-btn>
          <q-btn class="white text-blue-7" rounded icon="chevron_right" data-test="previsao-proximo" @click="deslocar(1)" />
        </q-btn-group>

        <q-btn
          flat
          dense
          rounded
          no-caps
          class="q-ml-sm q-px-md bg-white text-blue-7"
          icon="summarize"
          label="Vendas de cada dia (V04)"
          data-test="previsao-relatorio"
          @click="abrirRelatorio"
        />

        <q-btn flat dense rounded no-wrap v-ripple class="q-ml-sm q-px-md bg-white text-black" no-caps icon="more_vert" style="margin-right: 10px">
          <q-menu anchor="top right" self="top right">
            <q-list class="text-grey-8" style="min-width: 100px">
              <q-item aria-hidden="true">
                <q-item-section class="text-uppercase text-grey-7" style="font-size: 0.7rem">Mais Opções</q-item-section>
              </q-item>
              <q-item clickable v-close-popup aria-hidden="true" data-test="previsao-atualizar" @click="consultar(periodoParaApi())">
                <q-item-section>Atualizar</q-item-section>
              </q-item>
              <q-item clickable v-close-popup aria-hidden="true" @click="props.toggleFullscreen">
                <q-item-section>{{ props.inFullscreen ? "Minimizar" : "Maximizar Tela" }}</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </template>

      <template v-slot:body="props">
        <q-tr :props="props" :data-test="`previsao-dia-${props.row.data}`">
          <q-td key="data" :props="props">{{ formatDia(props.row.data) }}</q-td>
          <q-td key="quantidade" :props="props">{{ props.row.quantidade }}</q-td>
          <q-td key="bruto" :props="props">R$ {{ $currency(props.row.bruto) }}</q-td>
          <q-td key="taxa" :props="props">R$ {{ $currency(props.row.taxa) }}</q-td>
          <q-td key="tarifa" :props="props">R$ {{ $currency(props.row.tarifa) }}</q-td>
          <q-td key="liquido" :props="props" class="text-weight-bold">R$ {{ $currency(props.row.liquido) }}</q-td>
          <q-td key="situacao" :props="props">
            <q-chip dense square text-color="white" :color="situacaoDe(props.row).cor" :icon="situacaoDe(props.row).icone">
              {{ situacaoDe(props.row).rotulo }}
            </q-chip>
          </q-td>
        </q-tr>
      </template>

      <template v-slot:bottom-row>
        <q-tr style="background-color: #F7F7F7" data-test="previsao-totais">
          <q-td><strong>Total</strong></q-td>
          <q-td class="text-right"><strong>{{ totais.quantidade }}</strong></q-td>
          <q-td class="text-right"><strong>R$ {{ $currency(totais.bruto) }}</strong></q-td>
          <q-td class="text-right"><strong>R$ {{ $currency(totais.taxa) }}</strong></q-td>
          <q-td class="text-right"><strong>R$ {{ $currency(totais.tarifa) }}</strong></q-td>
          <q-td class="text-right"><strong>R$ {{ $currency(totais.liquido) }}</strong></q-td>
          <q-td class="text-caption text-grey-8" data-test="previsao-resumo">
            Creditado R$ {{ $currency(totais.creditado) }} · A creditar R$ {{ $currency(totais.previsto) }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </div>
</template>

<script>
import moment from "moment";
import axios from "@/axios-auth";

const ROTA = "/vendas-online/previsao-credito";

const MASCARA = "DD/MM/YYYY";

const MASCARA_API = "YYYY-MM-DD";

const SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const SEM_TOTAIS = { quantidade: 0, bruto: 0, taxa: 0, tarifa: 0, liquido: 0, creditado: 0, previsto: 0 };

const SITUACOES = {
  prevista: { rotulo: "Prevista", cor: "orange-8", icone: "schedule" },
  parcial: { rotulo: "Parcial", cor: "blue-7", icone: "hourglass_top" },
  creditada: { rotulo: "Creditada", cor: "positive", icone: "check_circle" }
};

const COLUNAS = [
  { name: "data", label: "Dia do crédito", field: "data", align: "left" },
  { name: "quantidade", label: "Vendas", field: "quantidade", align: "right" },
  { name: "bruto", label: "Bruto", field: "bruto", align: "right" },
  { name: "taxa", label: "Taxa do pagamento", field: "taxa", align: "right" },
  { name: "tarifa", label: "Tarifa DouraSoft", field: "tarifa", align: "right" },
  { name: "liquido", label: "Líquido", field: "liquido", align: "right" },
  { name: "situacao", label: "Situação", field: "situacao", align: "left" }
];

export default {
  name: "vendas-online-previsao",
  data() {
    return {
      MASCARA,
      columns: COLUNAS,
      periodo: { inicio: "", fim: "" },
      dias: [],
      totais: { ...SEM_TOTAIS },
      carregando: false,
      erro: null,
      relatorio: null,
      pedido: 0
    };
  },
  mounted() {
    this.consultar({});
  },
  methods: {
    async consultar(params) {
      const pedido = ++this.pedido;
      this.carregando = true;
      const res = (await axios.get(ROTA, { params })) || {};
      if (pedido !== this.pedido) return;
      this.carregando = false;

      if (res.error || !res.data || !res.data.data) {
        const dados = (res.error && res.error.response && res.error.response.data) || {};
        this.erro = dados.message || "Não foi possível carregar a previsão de crédito.";
        this.dias = [];
        this.totais = { ...SEM_TOTAIS };
        return;
      }

      const previsao = res.data.data;
      this.erro = null;
      this.dias = previsao.dias || [];
      this.relatorio = previsao.relatorio || null;
      this.totais = { ...SEM_TOTAIS, ...(previsao.totais || {}) };
      this.periodo = {
        inicio: moment(previsao.inicio, MASCARA_API).format(MASCARA),
        fim: moment(previsao.fim, MASCARA_API).format(MASCARA)
      };
    },
    periodoParaApi(periodo = this.periodo) {
      return {
        inicio: moment(periodo.inicio, MASCARA, true).format(MASCARA_API),
        fim: moment(periodo.fim, MASCARA, true).format(MASCARA_API)
      };
    },
    deslocar(sentido) {
      const inicio = moment(this.periodo.inicio, MASCARA, true);
      const fim = moment(this.periodo.fim, MASCARA, true);
      if (!inicio.isValid() || !fim.isValid()) return;

      this.consultar({
        inicio: inicio.add(sentido, "months").startOf("month").format(MASCARA_API),
        fim: fim.add(sentido, "months").endOf("month").format(MASCARA_API)
      });
    },
    alterar(lado, valor) {
      const periodo = { ...this.periodo, [lado]: valor };
      const inicio = moment(periodo.inicio, MASCARA, true);
      const fim = moment(periodo.fim, MASCARA, true);
      if (lado === "inicio" && inicio.isAfter(fim)) periodo.fim = periodo.inicio;
      if (lado === "fim" && fim.isBefore(inicio)) periodo.inicio = periodo.fim;

      if (this.$refs[lado]) this.$refs[lado].hide();
      this.consultar(this.periodoParaApi(periodo));
    },
    formatDia(data) {
      const dia = moment(data, MASCARA_API, true);

      return `${dia.format(MASCARA)} · ${SEMANA[dia.day()]}`;
    },
    abrirRelatorio() {
      if (!this.relatorio) {
        this.$q.notify({ message: "O relatório V04 não está no menu Relatórios desta empresa.", color: "orange", position: "top-right" });
        return;
      }
      if (!this.relatorio.permitido) {
        this.$q.notify({ message: "O relatório V04 pede a permissão Editar da Venda Online.", color: "orange", position: "top-right" });
        return;
      }

      this.$router.push(`/relatorios/${this.relatorio.id}`);
    },
    situacaoDe(dia) {
      return SITUACOES[dia.situacao] || SITUACOES.prevista;
    }
  }
};
</script>
